import React, { Suspense, lazy, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PartyPopper, MessageCircle, ArrowDown, MapPin, Users } from "lucide-react";
import { WHATSAPP_URL, INTEREST_HREF } from "../lib/constants";
import { trackNotifyClick, trackWhatsAppClick } from "../lib/track";
import { byFile, src, srcSet, webpSrcSet } from "../lib/gallery";
import { SHOWS_SO_FAR } from "../lib/editions";
import { ROUTES } from "../lib/seo";
import { soldOutLine } from "./Shows";
import { NEXT_ANCHOR } from "./NextSteps";

const HeroAmbient = lazy(() => import("./three/HeroAmbient"));

/**
 * The photograph hero. While a Bhaav Sangam show is on sale the home page
 * uses <ShowsHero> instead (components/Shows.jsx), with the poster and the
 * booking strip. Once the dates have passed this takes over: it names no date
 * and sells nothing. With `soldOut` (the editions that sold out, from
 * src/data/shows.js) it opens on that line, as the proof, and points down to
 * what a visitor can still do.
 */
export default function Hero({ soldOut = [] }) {
  const [enable3D, setEnable3D] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Never during the react-snap snapshot: a layer switched on by an effect
    // would be baked into the static HTML but absent from the first client
    // render, and that hydration mismatch makes React re-render the page.
    setEnable3D(!reduced && navigator.userAgent !== "ReactSnap");
  }, []);

  // Two frames, art-directed, because one cannot serve both shapes.
  //
  // The hero box is 100svh, so its aspect ratio swings from about 3:2 on a
  // laptop to 0.46:1 on a phone. object-cover fills that by cropping the
  // sides, and on a phone it keeps barely a third of a 3:2 photograph's width.
  //
  // One frame, from the photographer's retouched Delhi set (The Grand,
  // 23 October 2026), which the Delhi gallery was built from: Anshu seated at
  // the microphone, eyes open, smiling out at the hall, Ram on the screen
  // behind her. It is a portrait, so the layout is built around that: a panel
  // across the top of a phone, and from lg up the right half of the screen,
  // with the copy on the dark left half and nothing ever laid over her.
  const HERO = byFile("delhi-2026-10-044");
  const HERO_NARROW = HERO;

  return (
    <section className="relative w-full overflow-hidden bg-[#0F0A07] lg:min-h-[100svh]" data-testid="hero-section">
      {/* The photograph. On a phone or tablet it is a panel across the top of
          the screen that fades into the dark below, and the copy follows it.
          From lg up it fills the right three-fifths, blending into the dark
          left half where the copy sits, so no text ever crosses her. */}
      <div className="relative z-0 h-[58svh] min-h-[400px] lg:absolute lg:inset-y-0 lg:right-0 lg:left-[40%] lg:h-auto lg:min-h-0">
        <picture>
          {/* Phone and tablet portrait: the crowd frame. Ordered first because
              the browser takes the first matching <source>. */}
          <source media="(max-width: 1023px)" type="image/webp" srcSet={webpSrcSet(HERO_NARROW)} sizes="100vw" />
          <source media="(max-width: 1023px)" srcSet={srcSet(HERO_NARROW, "jpg")} sizes="100vw" />
          <source type="image/webp" srcSet={webpSrcSet(HERO)} sizes="100vw" />
          <img
            src={src(HERO, 2000)}
            srcSet={srcSet(HERO, "jpg")}
            sizes="100vw"
            /* One alt for both frames: <picture> cannot vary it, so it
               describes the evening rather than either photograph. */
            alt="Anshu Goyal seated at the microphone at Bhaav Sangam, The Grand, Delhi, smiling out at the hall with Ram on the screen behind her."
            width={HERO.width}
            height={HERO.height}
            fetchpriority="high"
            decoding="sync"
            /* Cropped to keep her face just above the middle of the panel. */
            className="w-full h-full object-cover object-[center_75%] lg:object-[center_88%]"
            style={{ backgroundImage: `url(${HERO.lqip})`, backgroundSize: "cover", backgroundPosition: "center" }}
          />
        </picture>
        {/* Phone and tablet: a touch of shade behind the nav, then the panel
            fades into the dark the copy sits on. */}
        <div className="absolute inset-0 lg:hidden" style={{
          background: "linear-gradient(180deg, rgba(15,10,7,0.5) 0%, rgba(15,10,7,0) 22%, rgba(15,10,7,0) 52%, rgba(15,10,7,0.6) 74%, rgba(15,10,7,0.95) 90%, #0F0A07 100%)"
        }} />
        {/* Laptop and up: the photograph's left edge dissolves into the dark
            the copy sits on; a touch of shade top and bottom for the nav. */}
        <div className="absolute inset-0 hidden lg:block" style={{
          background: "linear-gradient(90deg, #0F0A07 0%, rgba(15,10,7,0.7) 16%, rgba(15,10,7,0.2) 36%, rgba(15,10,7,0) 52%)"
        }} />
        <div className="absolute inset-0 hidden lg:block" style={{
          background: "linear-gradient(180deg, rgba(15,10,7,0.45) 0%, transparent 22%, transparent 78%, rgba(15,10,7,0.5) 100%)"
        }} />
      </div>

      {/* Subtle ambient particles on top */}
      {enable3D && (
        <Suspense fallback={null}>
          <div className="absolute inset-0 z-10 opacity-60 pointer-events-none">
            <HeroAmbient />
          </div>
        </Suspense>
      )}

      {/* The copy: under the photograph on a phone, in the left column from
          lg up, left-aligned either way so it never crosses the subject. */}
      <div className="relative z-20 flex flex-col items-start text-left px-6 -mt-14 pb-16 md:px-10 md:-mt-20 lg:mt-0 lg:min-h-[100svh] lg:justify-center lg:w-[46%] lg:max-w-[620px] lg:pl-[6vw] lg:pr-0 lg:pt-28 lg:pb-24">
        {soldOut.length ? (
          // The editions that sold out, as the proof, and the way to what comes
          // next. It is the first thing on a phone.
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex flex-col items-start"
            data-testid="hero-sold-out"
          >
            <p className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-body text-xs font-semibold tracking-[0.18em] uppercase sold-out-badge">
              Sold out
            </p>
            <p className="mt-3 max-w-md font-display text-lg md:text-xl leading-snug" style={{ color: "#F2EAD9" }}>
              {soldOutLine(soldOut)}
            </p>
            <a
              href={`#${NEXT_ANCHOR}`}
              className="mt-2 inline-flex items-center gap-1.5 font-body text-sm underline underline-offset-4 hover:opacity-80 transition"
              style={{ color: "#E5C880" }}
              data-testid="hero-next-link"
            >
              Bring Antara to your city, your event or your brand <ArrowDown size={14} />
            </a>
          </motion.div>
        ) : (
          /* City pill */
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md border"
            style={{ background: "rgba(201,169,97,0.15)", borderColor: "rgba(201,169,97,0.5)" }}
          >
            <MapPin size={13} style={{ color: "#E5C880" }} />
            <span className="font-body text-[11px] md:text-xs tracking-[0.18em] uppercase" style={{ color: "#F5EFE6" }}>
              Gurgaon · Delhi · Chandigarh
            </span>
          </motion.div>
        )}

        {/* H1 — direct, plain English */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-6 font-display leading-[0.98] tracking-tight"
          style={{ color: "#FBF6EE", fontSize: "clamp(2.4rem, 6vw, 4.75rem)" }}
          data-testid="hero-headline"
        >
          An Evening of <br />
          <span className="gold-grad italic">Live Bhajan Jamming</span>
        </motion.h1>

        {/* Sub — explains in 1 sentence what cold traffic is here for */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.45 }}
          className="mt-5 md:mt-7 font-body text-base md:text-lg max-w-xl leading-relaxed"
          style={{ color: "#F2EAD9" }}
        >
          A two-hour immersive bhajan experience where the audience becomes the choir.
          Live music. Real energy. No spectators — only participants.
        </motion.p>

        {/* PRIMARY CTA — nothing on sale, so hosting leads: Antara at the
            visitor's own occasion. The interest list is the second line. */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.65 }}
          className="mt-9 md:mt-11 flex flex-col items-start"
        >
          <Link to={ROUTES.host.path} className="cta-primary group" data-testid="hero-host-btn">
            <PartyPopper size={20} strokeWidth={1.6} />
            <span>Host Antara</span>
            <span className="cta-arrow">→</span>
          </Link>
          <p className="mt-3 font-body text-sm md:text-base font-medium" style={{ color: "#E5C880" }}>
            For your occasion: a wedding function, a sangeet, a home gathering, a corporate evening
          </p>
          <a
            href={INTEREST_HREF}
            onClick={trackNotifyClick}
            className="mt-5 inline-flex items-center gap-1.5 font-body text-sm md:text-base underline underline-offset-4 hover:opacity-80 transition"
            style={{ color: "#F2EAD9" }}
            data-testid="hero-interest-btn"
          >
            Or tell us your city, and we'll let you know when Antara comes to you <ArrowDown size={14} />
          </a>
        </motion.div>

        {/* Mini venue strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.85 }}
          className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 font-body text-xs md:text-sm"
          style={{ color: "#E8D9C5" }}
        >
          <div className="flex items-center gap-2">
            <Users size={14} style={{ color: "#C9A961" }} />
            <span>{SHOWS_SO_FAR} shows so far, from Gurgaon to Chandigarh</span>
          </div>
          <span className="hidden sm:block opacity-40">·</span>
          <div className="flex items-center gap-2">
            <MapPin size={14} style={{ color: "#C9A961" }} />
            <span>Latest: Tagore Theatre, Chandigarh</span>
          </div>
        </motion.div>

        {/* Secondary action — soft */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-8 flex items-center gap-5"
        >
          <a href="#what-is-it" className="font-body text-xs md:text-sm tracking-wide flex items-center gap-1.5 hover:opacity-80 transition" style={{ color: "#E5C880" }}>
            What is bhajan jamming?
            <ArrowDown size={13} className="animate-bounce" style={{ animationDuration: "1.6s" }} />
          </a>
          <span className="opacity-40" style={{ color: "#E5C880" }}>|</span>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackWhatsAppClick}
            className="font-body text-xs md:text-sm tracking-wide flex items-center gap-1.5 hover:opacity-80 transition"
            style={{ color: "#E5C880" }}
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle size={13} /> A question? WhatsApp
          </a>
        </motion.div>
      </div>
    </section>
  );
}
